import { useSyncExternalStore } from 'react';
import { Venue, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';
import { loadAllPages } from './loadAllPages';

export interface VenueListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: VenueListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useVenuesPage = (params: VenueListParams): PaginatedResponse<Venue> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('venues', cb),
    () => 0,
    () => 0
  );
  return ctx.read('venues', { kind: 'page', ...params }, (p: VenueListParams) =>
    api.listStaffVenues(buildQuery(p))
  );
};

export const useVenuesAll = (): Venue[] => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('venues', cb),
    () => 0,
    () => 0
  );
  return ctx.read('venues', { kind: 'all' }, () =>
    loadAllPages<Venue>((p) => api.listStaffVenues(p))
  );
};

export interface VenueMutations {
  create: (venue: Venue) => Promise<void>;
  update: (venue: Venue) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useVenueMutations = (): VenueMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('venues');
  return {
    create: async (venue) => {
      await api.createVenue(venue);
      after();
    },
    update: async (venue) => {
      await api.updateVenue(venue);
      after();
    },
    remove: async (id) => {
      await api.deleteVenue(id);
      after();
    }
  };
};
